"use client";

import { MotionValue, motion, useMotionValue, useScroll, useSpring, useTransform } from "motion/react";
import { useEffect, useMemo, useRef } from "react";
import { LogoMark } from "~/components/brand/LogoMark";

type ShapeType = "square" | "circle" | "triangle" | "c" | "logo";

type BaseShape = {
  id: string;
  type: ShapeType;
  gridRow: number;
  gridCol: number;
  size: number;
};

type AnimatedShape = BaseShape & {
  start: { x: number; y: number; rotate: number; rotateX: number; rotateY: number; scale: number };
  swarm: { x: number; y: number; rotate: number; rotateX: number; rotateY: number; scale: number };
  end: { x: number; y: number; rotate: number; rotateX: number; rotateY: number; scale: number };
};

const GRID_SIZE = 8;
const GRID_GAP = 60;

const manifestoParagraphs = [
  "When the word was coined in the 1950s, it meant something alive, malleable logic running on hard machines. Software and hardware, working together. But that's not what enterprise software became.",
  'Today, "enterprise software" means the opposite.',
  "It means rigid workflows, static forms, exception errors, and endless consulting hours. The tools meant to bring order buried companies under layers of complexity. ERPs, point solutions, and middleware promised universality and delivered entropy. Systems built to serve everyone ended up serving no one particularly well.",
  "One size fits none.",
  "The best companies have always known it. Amazon, Tesla, Stripe. They don't contort to someone else's system. They build their own. They turned software into a competitive advantage.",
  "For most, the only way to replicate this was to hire armies of developers or consultants, hard-code every rule, and pray it didn't break.",
  "AI changes that. AI brings software back to its origin story. It doesn't need perfect inputs or happy paths. It can learn, adapt, and act.",
  "Yet in the enterprise, raw AI is not enough. It must be governed.",
  "Left alone, it hallucinates; unshaped, it can't be trusted.",
  "The future isn't free-floating agents. It's agentic systems with structure.",
  "That's what CoPlane builds.",
  "A plane where agents, rules, and humans operate in concert.",
  "Agents execute, classify, and reason. Rules encode policies, guardrails, and approvals. Humans step in at the edge cases—with full context, full audit, and full control.",
  "CoPlane is where AI works for your company. It's the intelligent operations layer. A higher plane. Software that assembles around your data, your policies, and your people--and evolves as the business does.",
];

const beliefStatements = [
  "Custom is power. Every enterprise deserves systems contoured to its own shape, not bent to a vendor's template.",
  "AI is a catalyst. Use agents where they accelerate, anchor outcomes with code and rules, not as the first place to turn for everything.",
  "Code is your foundation. Once you've gathered data on how work gets done, use AI to crystallize it in code—for repeatability, leverage, and efficiency.",
  "Governance is non-negotiable. Every action should be explainable, auditable, and reversible.",
  "Time is leverage. Ship in weeks, iterate daily, compound continuously.",
];

const proofPoints = [
  "The result: systems that scale human judgment, eliminate repetitive work, and transform the back office into a durable competitive advantage.",
  "We've started on hard-mode going deep with complex, global enterprises—including Georgia-Pacific, The Kraft Group, Parts Authority, and Red Ventures—to deliver a new plane of enterprise software.",
  "Processes that learn under guardrails. Experts who improve logic directly. IT that multiplies its impact. Finance and operations that see every contour of the business: clear, fast, accountable.",
  "Faster to deploy. Safer to govern. Built to last.",
  "Every enterprise deserves systems shaped to its own contours. Go build the software only your company can have.",
];

export function ManifestoSection({ animated = true }: { animated?: boolean }) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  // Use section-local progress tied to the viewport top, so animation timing
  // is based on where the section is in view (not full page depth).
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 40, damping: 24, mass: 0.8 });
  const arrangementProgress = useTransform(smoothProgress, [0, 1], [0.02, 1]);
  const shapes = useMemo(() => (animated ? createAnimatedShapes() : createShapeGrid()), [animated]);

  // Rails are not pinned from here; RailStack observes sections and moves accordingly

  return (
    <div ref={sectionRef} className="relative w-full bg-white text-black pb-10">
      {animated ? (
        <ShapeFieldOverlay progress={arrangementProgress} shapes={shapes as AnimatedShape[]} />
      ) : null}

      {/* Article content flows naturally; full height controls when rails unfreeze */}
      <div className="relative mx-auto max-w-5xl px-6 lg:px-0">
        <article className="relative z-10 mx-auto max-w-[86vw] sm:max-w-[520px] md:max-w-[540px] pt-18 sm:pt-36 space-y-6 text-justify text-[17px] leading-[1.9] text-white mix-blend-difference md:space-y-8 md:text-[18px]">
          <div className="space-y-3 text-center">
            <p className="text-[12px] uppercase tracking-[0.4em] text-white/80">Manifesto</p>
            <h1 className="text-[34px] font-semibold tracking-tight text-current sm:text-[36px] md:text-[42px]">Software was Supposed to Be Malleable.</h1>
          </div>

          {manifestoParagraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}

          <section className="space-y-4">
            <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-white/80">We believe</p>
            <ul className="list-disc space-y-3 pl-5 text-[17px]">
              {beliefStatements.map((statement) => (
                <li className="text-justify" key={statement}>
                  {statement}
                </li>
              ))}
            </ul>
          </section>

          {proofPoints.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}

          <div className={`pt-6 ${animated ? "pb-[60vh]" : "pb-8"} text-center text-[13px] uppercase tracking-[0.45em]`}>Go CoPlane.</div>
        </article>
        {!animated ? (
          <div className="relative z-0 mx-auto max-w-4xl text-black mix-blend-normal">
            <StaticGrid shapes={shapes as BaseShape[]} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StaticGrid({ shapes }: { shapes: BaseShape[] }) {
  const isMobile = typeof window !== "undefined" ? window.innerWidth < 640 : false;
  const CELL = isMobile ? 38 : 45;
  const GAP = isMobile ? 10 : 15;
  return (
    <div className="mx-auto mt-10 mb-16 max-w-4xl">
      <div
        className="mx-auto"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${GRID_SIZE}, ${CELL}px)`,
          gap: `${GAP}px`,
          width: "fit-content",
        }}
      >
        {shapes.map((shape) => (
          <div key={shape.id} style={{ width: CELL, height: CELL }}>
            <ShapePrimitive size={CELL} type={shape.type} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ShapeFieldOverlay({ progress, shapes }: { progress: MotionValue<number>; shapes: AnimatedShape[] }) {
  const driftY = useTransform(progress, [0, 1], [0, 60]);
  const stickyHeight = typeof window !== "undefined" ? window.innerHeight - 32 : 800;
  const baseGridOffsetY = stickyHeight * 0.8;
  const approachY = useTransform(progress, [0, 1], [-stickyHeight * 0.8, 0]);
  const fieldY = useTransform([driftY, approachY], ([d, a]) => (d as number) + (a as number) + baseGridOffsetY);

  const stickyRef = useRef<HTMLDivElement | null>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      const el = stickyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      mouseX.set((e.clientX - cx) / rect.width);
      mouseY.set((e.clientY - cy) / rect.height);
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => window.removeEventListener("pointermove", onPointerMove);
  }, [mouseX, mouseY]);

  return (
    <div className="pointer-events-none absolute left-0 right-0" style={{ top: 8, bottom: 24 }} aria-hidden>
      <motion.div ref={stickyRef} className="sticky" style={{ top: 0, height: "calc(var(--vh, 1vh) * 100 - 32px)", y: fieldY }}>
        <div className="relative mx-auto h-full max-w-4xl">
          {shapes.map((shape) => (
            <FloatingShape key={shape.id} shape={shape} progress={progress} followX={mouseX} followY={mouseY} />
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function FloatingShape({
  shape,
  progress,
  followX,
  followY,
}: {
  shape: AnimatedShape;
  progress: MotionValue<number>;
  followX: MotionValue<number>;
  followY: MotionValue<number>;
}) {
  const positionProgress = useTransform(progress, (value) => easeOutCubic(clamp(value as number, 0, 1)));

  const x = useTransform(positionProgress, (p) => lerp(shape.start.x, shape.end.x, p as number));
  const y = useTransform(positionProgress, (p) => lerp(shape.start.y, shape.end.y, p as number));
  const rotate = useTransform(positionProgress, (p) => lerp(shape.start.rotate, shape.end.rotate, p as number));
  const rotateX = useTransform(positionProgress, (p) => lerp(shape.start.rotateX, shape.end.rotateX, p as number));
  const rotateY = useTransform(positionProgress, (p) => lerp(shape.start.rotateY, shape.end.rotateY, p as number));
  const scale = useTransform(positionProgress, (p) => lerp(shape.start.scale, shape.end.scale, p as number));
  const opacity = useTransform(progress, [0, 0.05, 0.25, 0.95, 1], [0, 0.2, 0.5, 1, 1]);

  const settle = useTransform(positionProgress, [0.9, 1], [0, 1]);
  const followIntensity = 10;
  const extraX = useTransform([followX, settle], ([mx, s]) => (shape.type === "logo" ? (mx as number) * followIntensity * (s as number) : 0));
  const extraY = useTransform([followY, settle], ([my, s]) => (shape.type === "logo" ? (my as number) * followIntensity * (s as number) : 0));
  const xWithFollow = useTransform([x, extraX], ([bx, ex]) => (bx as number) + (ex as number));
  const yWithFollow = useTransform([y, extraY], ([by, ey]) => (by as number) + (ey as number));

  return (
    <motion.div className="absolute left-1/2 top-1/2 z-0 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center" style={{ x: xWithFollow, y: yWithFollow, rotate, rotateX, rotateY, scale, opacity }}>
      <ShapePrimitive size={shape.size} type={shape.type} />
    </motion.div>
  );
}

function ShapePrimitive({ type, size }: { type: ShapeType; size: number }) {
  if (type === "circle") {
    return <div style={{ width: size, height: size }} className="rounded-full bg-black" />;
  }
  if (type === "triangle") {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" className="fill-black">
        <polygon points="50 10 94 88 6 88" />
      </svg>
    );
  }
  if (type === "c") {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
        <path d="M99.4695 68.099C91.2467 88.2812 72.9443 100 51.7241 100C22.2812 100 0 77.0833 0 50.1302C0 21.7448 22.5464 0 51.061 0C73.6074 0 93.5013 13.6719 100 33.4635H72.0159C67.1088 27.3437 59.1512 23.9583 51.3263 23.9583C37.2679 23.9583 25.0663 35.9375 25.0663 49.7396C25.0663 64.3229 37.4005 76.0417 51.4589 76.0417C58.7533 76.0417 65.3846 73.3073 70.6897 68.099H99.4695ZM51.7241 72.6562C38.5942 72.6562 28.5146 62.1094 28.5146 50C28.5146 37.8906 38.5942 27.3438 51.7241 27.3438C64.8541 27.3438 74.8011 37.3698 74.8011 50C74.8011 62.6302 64.4562 72.6562 51.7241 72.6562ZM56.8966 50.2604C56.8966 47.2656 54.7745 44.9219 51.5915 44.9219C48.8064 44.9219 46.4191 47.2656 46.4191 50.2604C46.4191 53.2552 48.939 55.7292 51.7241 55.7292C54.7745 55.7292 56.8966 53.2552 56.8966 50.2604Z" fill="black" />
      </svg>
    );
  }
  if (type === "logo") {
    return <LogoMark height={size} className="text-black" />;
  }
  return <div style={{ width: size, height: size }} className="bg-black" />;
}

function createAnimatedShapes(): AnimatedShape[] {
  const baseShapes = createShapeGrid();
  const gridExtent = (GRID_SIZE - 1) * GRID_GAP;
  const originX = -gridExtent / 2;
  const originY = -gridExtent / 2;
  const VIEW_W = typeof window !== "undefined" ? Math.min(window.innerWidth, 1024) : 1024;
  const VIEW_H = typeof window !== "undefined" ? Math.max(560, window.innerHeight - 96) : 800;
  const START_SPREAD_X = VIEW_W * 0.9;
  const START_SPREAD_Y = VIEW_H * 0.9;

  return baseShapes.map((shape, index) => {
    const endX = originX + shape.gridCol * GRID_GAP;
    const endY = originY + shape.gridRow * GRID_GAP;
    const startX = (Math.random() * 2 - 1) * (START_SPREAD_X / 2);
    const startY = (Math.random() * 2 - 1) * (START_SPREAD_Y / 2);
    const startScale = 0.6 + Math.random() * 1.4;
    const startRotate = (Math.random() - 0.5) * 180;
    const startRotateX = (Math.random() - 0.5) * 60;
    const startRotateY = (Math.random() - 0.5) * 60;

    return {
      ...shape,
      start: { x: startX, y: startY, rotate: startRotate, rotateX: startRotateX, rotateY: startRotateY, scale: startScale },
      swarm: { x: startX, y: startY + (index % 3) * 8, rotate: startRotate * 0.7, rotateX: startRotateX * 0.7, rotateY: startRotateY * 0.7, scale: Math.max(0.8, Math.min(1.8, startScale * 0.9)) },
      end: { x: endX, y: endY, rotate: 0, rotateX: 0, rotateY: 0, scale: 1 },
    };
  });
}

function createShapeGrid(): BaseShape[] {
  const shapes: BaseShape[] = [];
  const circle = { row: 1, col: Math.floor(GRID_SIZE / 2) };
  const triangle = { row: GRID_SIZE - 1, col: 1 };
  const cShape = { row: GRID_SIZE - 1, col: GRID_SIZE - 2 };

  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      let type: ShapeType = "square";
      if (row === circle.row && col === circle.col) type = "circle";
      if (row === triangle.row && col === triangle.col) type = "triangle";
      if (row === cShape.row && col === cShape.col) type = "c";
      shapes.push({ id: `shape-${row}-${col}`, type, gridRow: row, gridCol: col, size: 45 });
    }
  }
  const squareIndices = shapes.map((s, i) => (s.type === "square" ? i : -1)).filter((i) => i >= 0);
  if (squareIndices.length > 0) {
    const chosen = squareIndices[Math.floor(Math.random() * squareIndices.length)];
    shapes[chosen] = { ...shapes[chosen], type: "logo" };
  }
  return shapes;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const lerp = (start: number, end: number, progress: number) => start + (end - start) * progress;
const easeOutCubic = (value: number) => 1 - Math.pow(1 - value, 3);

export default ManifestoSection;


